import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';

const CMSOrderDetail = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const loadOrder = async () => {
    try {
      const res = await api.getEcommerceOrderById(id);
      if (res.success) {
        setOrder(res.data);
        setStatus(res.data.status || 'pending');
        setPaymentStatus(res.data.payment_status || 'pending');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrder();
  }, [id]);

  const handleStatusUpdate = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await api.updateEcommerceOrderStatus(id, {
        status,
        payment_status: paymentStatus
      });
      if (res.success) {
        setOrder(res.data);
        setSaveMsg('Order updated');
      }
    } catch (e) {
      setSaveMsg(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) {
    return (
      <div className="alert alert-danger">
        {error}
        <Link to="/cms/orders" className="btn btn-sm btn-default ml-2">Back to Orders</Link>
      </div>
    );
  }
  if (!order) return <div className="text-center p-4">Order not found</div>;

  const items = order.items || [];

  return (
    <>
      <div className="row mb-3">
        <div className="col-md-12">
          <Link to="/cms/orders" className="btn btn-default btn-sm">
            <i className="fas fa-arrow-left"></i> Back to Orders
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Order</h3>
              <span className="badge bg-secondary">{order.status}</span>
            </div>
            <div className="card-body">
              <p><strong>Order ID:</strong> {order.id}</p>
              <p><strong>Order number:</strong> <code>{order.order_number}</code></p>
              <p><strong>Created:</strong> {order.created_at ? new Date(order.created_at).toLocaleString() : '—'}</p>
              {order.payment_method && <p><strong>Payment method:</strong> {order.payment_method}</p>}
              {order.shipping_method && <p><strong>Shipping:</strong> {order.shipping_method}</p>}
              <hr />
              <div className="form-group">
                <label>Order Status</label>
                <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {['pending', 'confirmed', 'paid', 'picking', 'packing', 'shipped', 'delivered', 'cancelled', 'returned'].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Payment Status</label>
                <select className="form-control" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                  {['pending', 'paid', 'failed', 'refunded'].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={handleStatusUpdate}>
                {saving ? 'Saving…' : 'Update Status'}
              </button>
              {saveMsg && <span className="ml-2 text-muted">{saveMsg}</span>}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Customer</h3>
            </div>
            <div className="card-body">
              <p><strong>Name:</strong> {order.customer_name}</p>
              <p><strong>Email:</strong> {order.customer_email}</p>
              <p><strong>Phone:</strong> {order.customer_phone || '—'}</p>
              {order.shipping_address && (
                <p><strong>Shipping address:</strong><br />{order.shipping_address}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Products ordered</h3>
        </div>
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Qty</th>
                <th>Unit price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">No items</td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.product_name}</td>
                    <td><code>{item.sku || '—'}</code></td>
                    <td>{item.quantity}</td>
                    <td>{Number(item.unit_price).toFixed(2)}</td>
                    <td>{Number(item.total).toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <div className="row">
            <div className="col-md-6"></div>
            <div className="col-md-6 text-right">
              <p className="mb-0"><strong>Subtotal:</strong> {Number(order.subtotal).toFixed(2)}</p>
              {order.shipping_cost != null && Number(order.shipping_cost) > 0 && (
                <p className="mb-0"><strong>Shipping:</strong> {Number(order.shipping_cost).toFixed(2)}</p>
              )}
              {order.tax != null && Number(order.tax) !== 0 && (
                <p className="mb-0"><strong>Tax:</strong> {Number(order.tax).toFixed(2)}</p>
              )}
              <p className="mb-0"><strong>Total:</strong> {Number(order.total).toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {order.activity?.length > 0 && (
        <div className="card">
          <div className="card-header"><h3 className="card-title">Timeline</h3></div>
          <div className="card-body">
            <ul className="list-unstyled mb-0">
              {order.activity.map((a) => (
                <li key={a.id} className="mb-2">
                  <strong>{a.action}</strong> — {a.details}
                  <br /><small className="text-muted">{new Date(a.created_at).toLocaleString()}</small>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {order.notes && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Notes</h3>
          </div>
          <div className="card-body">
            <p className="mb-0">{order.notes}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default CMSOrderDetail;
